import { gateAndClassify } from "./gate.js";
const FFT_SIZE = 512;
/** Number of raw samples per analysis frame (exposed for offline capture). */
export const ANALYSIS_FRAME_SIZE = FFT_SIZE;
export const DEFAULT_DSP_CONFIG = {
    energyGate: 0.06,
    energyScale: 3,
    noiseBandLoHz: 1500,
    noiseBandHiHz: 7000,
    voicingBandHiHz: 1000,
    transientScale: 4,
    precisionSoftness: 3,
    frameHopMs: 23,
    pitchWindowSamples: 2048,
    pitchMinHz: 70,
    pitchMaxHz: 1000,
    pitchClarityGate: 0.7,
    voicingGateForPitch: 0.2,
    flatnessGateForPitch: 0.7,
};
export const dspConfig = { ...DEFAULT_DSP_CONFIG };
export function setDspConfig(patch) {
    Object.assign(dspConfig, patch);
    if (!(dspConfig.energyScale > 0.1)) {
        console.warn(`[dsp] energyScale was set to ${dspConfig.energyScale}; ` +
            `clamping to 0.1 (zero/near-zero makes energy a dead feature).`);
        dspConfig.energyScale = 0.1;
    }
    if (!(dspConfig.pitchMinHz >= 20))
        dspConfig.pitchMinHz = 20;
    if (!(dspConfig.pitchMaxHz > dspConfig.pitchMinHz)) {
        console.warn(`[dsp] pitchMaxHz (${dspConfig.pitchMaxHz}) <= pitchMinHz ` +
            `(${dspConfig.pitchMinHz}); resetting to a valid range.`);
        dspConfig.pitchMaxHz = Math.max(dspConfig.pitchMinHz + 10, DEFAULT_DSP_CONFIG.pitchMaxHz);
    }
    if (!(dspConfig.pitchWindowSamples >= 256)) {
        dspConfig.pitchWindowSamples = DEFAULT_DSP_CONFIG.pitchWindowSamples;
    }
    console.info("[dsp] config updated", { ...dspConfig });
}
export class Analyzer {
    constructor(sampleRate, cfg = dspConfig) {
        Object.defineProperty(this, "sampleRate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: sampleRate
        });
        Object.defineProperty(this, "cfg", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: cfg
        });
        Object.defineProperty(this, "satFrames", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "lastSatWarn", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "re", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Float32Array(FFT_SIZE)
        });
        Object.defineProperty(this, "im", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Float32Array(FFT_SIZE)
        });
        Object.defineProperty(this, "window", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Float32Array(FFT_SIZE)
        });
        Object.defineProperty(this, "prevEnergy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "windowPower", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pitchWindow", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pitchWrite", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "nsdf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "nsdfWork", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "prevMag", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Float32Array(FFT_SIZE / 2)
        });
        Object.defineProperty(this, "lastNsdfPeak", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        let winPow = 0;
        for (let i = 0; i < FFT_SIZE; i++) {
            this.window[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (FFT_SIZE - 1)));
            winPow += this.window[i] * this.window[i];
        }
        this.windowPower = winPow / FFT_SIZE;
        const win = Math.max(256, this.cfg.pitchWindowSamples | 0);
        this.pitchWindow = new Float32Array(win);
        this.nsdf = new Float32Array(win);
        this.nsdfWork = new Float64Array(win);
    }
    extract(samples) {
        const re = this.re;
        const im = this.im;
        for (let i = 0; i < FFT_SIZE; i++) {
            re[i] = (samples[i] ?? 0) * this.window[i];
            im[i] = 0;
        }
        fft(re, im);
        const pw = this.pitchWindow;
        const pwLen = pw.length;
        const nSamp = Math.min(samples.length, FFT_SIZE);
        for (let i = 0; i < nSamp; i++) {
            pw[this.pitchWrite] = samples[i] ?? 0;
            this.pitchWrite = (this.pitchWrite + 1) % pwLen;
        }
        const nyquist = this.sampleRate / 2;
        const bins = FFT_SIZE / 2;
        let sumMag = 0;
        let sumFreqMag = 0;
        let energy = 0;
        let noiseBand = 0;
        let lowBand = 0;
        let highBand = 0;
        let logMagSum = 0;
        let flatnessCount = 0;
        let flux = 0;
        let peakMag = 0;
        let sumFreqSqMag = 0;
        const { noiseBandLoHz, noiseBandHiHz, voicingBandHiHz, energyScale, transientScale, } = this.cfg;
        for (let k = 1; k < bins; k++) {
            const mag = Math.hypot(re[k], im[k]);
            const freq = (k / bins) * nyquist;
            sumMag += mag;
            sumFreqMag += mag * freq;
            energy += mag * mag;
            if (freq >= noiseBandLoHz && freq <= noiseBandHiHz)
                noiseBand += mag;
            if (freq < voicingBandHiHz)
                lowBand += mag;
            if (freq >= voicingBandHiHz)
                highBand += mag;
            logMagSum += Math.log(mag + 1e-9);
            flatnessCount++;
            // Spectral flux: positive-only magnitude change vs. previous frame.
            const diff = mag - this.prevMag[k];
            if (diff > 0)
                flux += diff;
            this.prevMag[k] = mag;
            if (mag > peakMag)
                peakMag = mag;
            sumFreqSqMag += mag * freq * freq;
        }
        const centroidHz = sumMag > 1e-9 ? sumFreqMag / sumMag : 0;
        const centroid = clamp01(centroidHz / nyquist);
        // Spectral spread: sqrt of the magnitude-weighted variance of freq
        // about the centroid, normalized to the Nyquist range.
        let spread = 0;
        if (sumMag > 1e-9) {
            const meanSq = sumFreqSqMag / sumMag;
            const varHz = Math.max(0, meanSq - centroidHz * centroidHz);
            spread = clamp01(Math.sqrt(varHz) / (nyquist * 0.5));
        }
        // Crest factor: peak-to-mean magnitude ratio, a tonality/impulse cue.
        const meanMag = sumMag / Math.max(1, flatnessCount);
        const crest = clamp01(meanMag > 1e-9 ? Math.log1p(peakMag / meanMag) / Math.log(bins) : 0);
        // Normalized spectral flux (onset/change energy).
        const fluxNorm = clamp01(flux / (sumMag + 1e-9));
        const rms = Math.sqrt((2 * energy) / (bins * bins * (this.windowPower || 0.375)));
        const nrg = clamp01(Math.tanh(rms * energyScale));
        const noise = clamp01(noiseBand / (sumMag + 1e-9));
        const voicing = clamp01(lowBand / (sumMag + 1e-9));
        const rise = Math.max(0, nrg - this.prevEnergy);
        const fromQuiet = this.prevEnergy < 0.25 ? 1 : 0.15;
        const transient = clamp01(rise * transientScale * fromQuiet);
        this.prevEnergy = nrg;
        const arithMean = sumMag / Math.max(1, flatnessCount);
        const geoMean = flatnessCount > 0 ? Math.exp(logMagSum / flatnessCount) : 0;
        const flatness = clamp01(arithMean > 1e-9 ? geoMean / arithMean : 0);
        let rolloff = 0;
        if (sumMag > 1e-9) {
            const threshold = sumMag * 0.85;
            let acc = 0;
            for (let k = 1; k < bins; k++) {
                acc += Math.hypot(re[k], im[k]);
                if (acc >= threshold) {
                    rolloff = clamp01(((k / bins) * nyquist) / nyquist);
                    break;
                }
            }
        }
        const tilt = clamp01(highBand / (sumMag + 1e-9));
        // Zero-crossing rate over the FFT frame (windowless raw samples), a
        // classic noisiness/frication indicator that is cheap and pitch-free.
        let zcCount = 0;
        const zcN = Math.min(samples.length, FFT_SIZE);
        let prevSign = 0;
        for (let i = 0; i < zcN; i++) {
            const v = samples[i] ?? 0;
            const sign = v > 0 ? 1 : v < 0 ? -1 : 0;
            if (sign !== 0 && prevSign !== 0 && sign !== prevSign)
                zcCount++;
            if (sign !== 0)
                prevSign = sign;
        }
        const zcr = clamp01(zcN > 1 ? zcCount / (zcN - 1) : 0);
        const pitched = nrg > this.cfg.energyGate &&
            voicing > this.cfg.voicingGateForPitch &&
            flatness < this.cfg.flatnessGateForPitch;
        let pitchHz = 0;
        let pitchConf = 0;
        let pitchClass = -1;
        if (pitched) {
            const est = this.estimatePitch();
            if (est.hz > 0) {
                pitchHz = est.hz;
                pitchConf = est.clarity;
                const midi = 69 + 12 * Math.log2(pitchHz / 440);
                pitchClass = ((midi % 12) + 12) % 12;
            }
        }
        // Harmonic-to-noise ratio: the strongest NSDF peak found during the
        // last pitch search maps clarity → a periodicity confidence usable
        // even when the pitch gate rejected the frame. Falls back to voicing.
        const hnr = clamp01(Math.max(this.lastNsdfPeak, pitched ? pitchConf : 0));
        if (nrg > 0.985) {
            this.satFrames++;
            const now = typeof performance !== "undefined" ? performance.now() : Date.now();
            if (now - this.lastSatWarn > 3000 && this.satFrames > 30) {
                this.lastSatWarn = now;
                console.warn(`[dsp] ⚠ energy SATURATED (≈1.0) on ${this.satFrames} ` +
                    `recent frames at energyScale=${energyScale}. Loud sounds ` +
                    `are pinned to 1.0, so energy can't distinguish them and ` +
                    `silence sits outside the learned space (collapses model ` +
                    `to one class). Lower "Loudness gain" until loud sounds ` +
                    `read ~0.7, then recapture.`);
                this.satFrames = 0;
            }
        }
        return {
            centroid,
            noise,
            voicing,
            energy: nrg,
            transient,
            flatness,
            rolloff,
            tilt,
            spread,
            crest,
            flux: fluxNorm,
            zcr,
            hnr,
            pitchHz,
            pitchConf,
            pitchClass,
        };
    }
    estimatePitch() {
        const pw = this.pitchWindow;
        const n = pw.length;
        const buf = this.nsdf;
        for (let i = 0; i < n; i++) {
            buf[i] = pw[(this.pitchWrite + i) % n];
        }
        const lagMin = Math.max(1, Math.floor(this.sampleRate / this.cfg.pitchMaxHz));
        const lagMax = Math.min(n - 1, Math.ceil(this.sampleRate / this.cfg.pitchMinHz));
        if (lagMax <= lagMin)
            return { hz: 0, clarity: 0 };
        if (this.nsdfWork.length < lagMax + 1) {
            this.nsdfWork = new Float64Array(lagMax + 1);
        }
        const nsdf = this.nsdfWork;
        nsdf.fill(0, 0, lagMax + 1);
        for (let tau = lagMin; tau <= lagMax; tau++) {
            let acf = 0;
            let m = 0;
            const lim = n - tau;
            for (let i = 0; i < lim; i++) {
                const a = buf[i];
                const b = buf[i + tau];
                acf += a * b;
                m += a * a + b * b;
            }
            nsdf[tau] = m > 1e-9 ? (2 * acf) / m : 0;
        }
        const gate = this.cfg.pitchClarityGate;
        let bestTau = -1;
        let bestVal = 0;
        let tau = lagMin;
        while (tau <= lagMax && nsdf[tau] > 0)
            tau++;
        let globalPeak = 0;
        while (tau <= lagMax) {
            if (nsdf[tau] > 0) {
                let peakTau = tau;
                let peakVal = nsdf[tau];
                while (tau <= lagMax && nsdf[tau] > 0) {
                    if (nsdf[tau] > peakVal) {
                        peakVal = nsdf[tau];
                        peakTau = tau;
                    }
                    tau++;
                }
                if (peakVal > globalPeak)
                    globalPeak = peakVal;
                if (peakVal >= gate) {
                    bestTau = peakTau;
                    bestVal = peakVal;
                    break;
                }
            }
            else {
                tau++;
            }
        }
        this.lastNsdfPeak = clamp01(globalPeak);
        if (bestTau < lagMin || bestVal < gate)
            return { hz: 0, clarity: 0 };
        let refined = bestTau;
        const y0 = nsdf[bestTau - 1] ?? bestVal;
        const y1 = nsdf[bestTau];
        const y2 = nsdf[bestTau + 1] ?? bestVal;
        const denom = y0 - 2 * y1 + y2;
        if (Math.abs(denom) > 1e-9) {
            refined = bestTau + (0.5 * (y0 - y2)) / denom;
        }
        const hz = refined > 0 ? this.sampleRate / refined : 0;
        if (hz < this.cfg.pitchMinHz ||
            hz > this.cfg.pitchMaxHz ||
            !Number.isFinite(hz)) {
            return { hz: 0, clarity: 0 };
        }
        return { hz, clarity: clamp01(bestVal) };
    }
}
export function silence() {
    return {
        centroid: 0,
        noise: 0,
        voicing: 0,
        energy: 0,
        transient: 0,
        flatness: 0,
        rolloff: 0,
        tilt: 0,
        spread: 0,
        crest: 0,
        flux: 0,
        zcr: 0,
        hnr: 0,
        pitchHz: 0,
        pitchConf: 0,
        pitchClass: -1,
    };
}
const DIMS = [
    "centroid",
    "noise",
    "voicing",
    "energy",
    "transient",
    "flatness",
    "rolloff",
    "tilt",
    "spread",
    "crest",
    "flux",
    "zcr",
    "hnr",
];
const DIM_WEIGHT = {
    centroid: 1,
    noise: 1,
    voicing: 1,
    energy: 0.8,
    transient: 2.2,
    flatness: 1.6,
    rolloff: 1.3,
    tilt: 1.0,
    spread: 1.2,
    crest: 1.4,
    flux: 1.5,
    zcr: 1.3,
    hnr: 1.4,
};
function clusterDistance(f, c) {
    let d = 0;
    for (const k of DIMS) {
        const v = Math.max(c.variance[k], 1e-3);
        const diff = f[k] - c.center[k];
        d += (DIM_WEIGHT[k] * diff * diff) / (v * v);
    }
    return d;
}
export function classify(f, clusters) {
    if (f.energy < dspConfig.energyGate || clusters.length === 0) {
        return {
            clusterId: null,
            confidence: 0,
            delta: { precision: 0, consistency: 0 },
        };
    }
    let best = -1;
    let bestD = Infinity;
    let secondD = Infinity;
    let silenceD = Infinity;
    for (let i = 0; i < clusters.length; i++) {
        const d = clusterDistance(f, clusters[i]);
        if (clusters[i].id === "silence") {
            silenceD = d;
            continue;
        }
        if (d < bestD) {
            secondD = bestD;
            bestD = d;
            best = i;
        }
        else if (d < secondD) {
            secondD = d;
        }
    }
    if (best < 0) {
        return {
            clusterId: null,
            confidence: 0,
            delta: { precision: 0, consistency: 0 },
        };
    }
    if (clusters[best].id === "pop") {
        const popCenter = clusters[best].center;
        const onsetGate = Math.max(0.3, popCenter.transient * 0.55);
        const tooVoiced = f.voicing > Math.max(0.5, popCenter.voicing + 0.15);
        if (f.transient < onsetGate || tooVoiced) {
            let altBest = -1;
            let altBestD = Infinity;
            for (let i = 0; i < clusters.length; i++) {
                const id = clusters[i].id;
                if (id === "pop" || id === "silence")
                    continue;
                const d = clusterDistance(f, clusters[i]);
                if (d < altBestD) {
                    altBestD = d;
                    altBest = i;
                }
            }
            if (altBest < 0) {
                return {
                    clusterId: null,
                    confidence: 0,
                    delta: { precision: 0, consistency: 0 },
                };
            }
            best = altBest;
            bestD = altBestD;
        }
    }
    if (Number.isFinite(silenceD) && silenceD * 1.15 <= bestD) {
        return {
            clusterId: null,
            confidence: 0,
            delta: { precision: 0, consistency: 0 },
        };
    }
    const margin = secondD === Infinity
        ? 1
        : clamp01((secondD - bestD) / (secondD + bestD + 1e-6));
    const separability = clusters.length === 1 ? 1 : margin;
    const precision = clamp01(1 - bestD / (bestD + dspConfig.precisionSoftness));
    return {
        clusterId: clusters[best].id,
        confidence: clamp01(0.4 + 0.6 * separability),
        delta: { precision, consistency: 0 },
    };
}
export function makeEvent(t, f, clusters, consistency) {
    const { clusterId, confidence, delta } = classifyFrame(f, clusters);
    return {
        t,
        clusterId,
        confidence,
        delta: { precision: delta.precision, consistency },
        features: f,
    };
}
let activeClassifier = null;
export function setActiveClassifier(c) {
    activeClassifier = c && c.ready ? c : null;
    activeClassifierDspFp = dspFingerprintForInference();
    console.info(`[dsp] active classifier ${activeClassifier ? "set" : "cleared"} ` +
        `dspFingerprint=${activeClassifierDspFp}`);
}
let activeClassifierDspFp = "";
/**
 * Single source of truth for the extraction-relevant DSP fingerprint. A
 * feature vector is only comparable to others produced under the SAME
 * extraction parameters, so this fingerprint gates corpus reuse, model
 * loading, and live drift detection. Callers (diagnostics.ts) MUST use this
 * rather than re-deriving it, or the three copies will silently rot apart.
 */
export function dspFingerprint(c = dspConfig) {
    return [
        "fv5",
        c.energyGate,
        c.energyScale,
        c.noiseBandLoHz,
        c.noiseBandHiHz,
        c.voicingBandHiHz,
        c.transientScale,
        c.frameHopMs,
        c.pitchWindowSamples,
        c.pitchMinHz,
        c.pitchMaxHz,
        c.pitchClarityGate,
        c.voicingGateForPitch,
        c.flatnessGateForPitch,
    ].join("/");
}
function dspFingerprintForInference() {
    return dspFingerprint(dspConfig);
}
function classifyFrame(f, clusters) {
    if (activeClassifier) {
        const liveFp = dspFingerprintForInference();
        if (activeClassifierDspFp && liveFp !== activeClassifierDspFp) {
            warnDspDriftOnce(activeClassifierDspFp, liveFp);
        }
        return gateAndClassify(f, activeClassifier);
    }
    if (f.energy < dspConfig.energyGate) {
        return {
            clusterId: null,
            confidence: 0,
            delta: { precision: 0, consistency: 0 },
        };
    }
    return classify(f, clusters);
}
function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
}
let lastDriftWarnKey = "";
function warnDspDriftOnce(trained, live) {
    const key = `${trained}=>${live}`;
    if (key === lastDriftWarnKey)
        return;
    lastDriftWarnKey = key;
    console.error("[dsp] FEATURE-SPACE DRIFT: the active classifier was trained/loaded " +
        `under DSP config '${trained}' but inference is now running under ` +
        `'${live}'. Feature vectors are extraction-config dependent, so the ` +
        "model is being fed vectors from a DIFFERENT space than it learned. " +
        "This is the likely cause of a model collapsing to a single class " +
        "(e.g. everything reads as 'hum'). The most common trigger is the " +
        "startup mic-gain probe changing energyScale AFTER a persisted model " +
        "was loaded. Retrain (recapture all sounds) under the current config, " +
        "or restore the DSP config the model was trained under.");
}
function fft(re, im) {
    const n = re.length;
    for (let i = 1, j = 0; i < n; i++) {
        let bit = n >> 1;
        for (; j & bit; bit >>= 1)
            j ^= bit;
        j ^= bit;
        if (i < j) {
            [re[i], re[j]] = [re[j], re[i]];
            [im[i], im[j]] = [im[j], im[i]];
        }
    }
    for (let len = 2; len <= n; len <<= 1) {
        const ang = (-2 * Math.PI) / len;
        const wr = Math.cos(ang);
        const wi = Math.sin(ang);
        for (let i = 0; i < n; i += len) {
            let cwr = 1;
            let cwi = 0;
            for (let k = 0; k < len / 2; k++) {
                const a = i + k;
                const b = i + k + len / 2;
                const tr = re[b] * cwr - im[b] * cwi;
                const ti = re[b] * cwi + im[b] * cwr;
                re[b] = re[a] - tr;
                im[b] = im[a] - ti;
                re[a] += tr;
                im[a] += ti;
                const ncwr = cwr * wr - cwi * wi;
                cwi = cwr * wi + cwi * wr;
                cwr = ncwr;
            }
        }
    }
}
